/**
 * Bilingual NL/EN field that shows ONLY the active UI-language input. The
 * other-language counterpart is hidden in the UI but kept in sync via
 * Claude-translate on blur (last-edit-wins: blurring the visible field
 * sends its text to /translate and writes the result to the hidden side).
 *
 * Used for `name` (single-line input) and `description` (multi-line textarea).
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../../api/client";

type Lang = "nl" | "en";

interface Props {
  labelNl: string;
  labelEn: string;
  valueNl: string;
  valueEn: string;
  multiline?: boolean | undefined;
  disabled?: boolean | undefined;
  /** "name" → short label translation, "description" → longer text translation. */
  kind: "name" | "description";
  onChange: (lang: Lang, value: string) => void;
  /** Optioneel — vuurt op COMMIT (blur, ná de eventuele auto-translate). Voor
   *  bv. de relatie-naam: dan pas de duplicaat-check draaien (niet per toets). */
  onCommit?: () => void;
}

const inputClass =
  "rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none disabled:bg-(--ld-raised) disabled:text-(--ld-muted)";

export function BilingualField(props: Props) {
  const { labelNl, labelEn, valueNl, valueEn, multiline, disabled, kind, onChange } = props;
  const { i18n, t } = useTranslation();
  // Actieve UI-taal — bepaalt welk veld zichtbaar is. De andere taal-
  // versie blijft in de DB-record (en wordt op blur via Claude vertaald).
  const activeLang: Lang = i18n.language === "en" ? "en" : "nl";
  const [translatingTo, setTranslatingTo] = useState<Lang | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsKey, setNeedsKey] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const timerRef = blurTimer;
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const onFocus = () => {
    setError(null);
  };

  const onBlur = (lang: Lang, currentValue: string) => {
    if (blurTimer.current) {
      clearTimeout(blurTimer.current);
      blurTimer.current = null;
    }
    blurTimer.current = setTimeout(() => {
      void maybeTranslate(lang, currentValue).finally(() => {
        props.onCommit?.();
      });
    }, 200);
  };

  const maybeTranslate = async (sourceLang: Lang, currentSourceValue: string) => {
    const targetLang: Lang = sourceLang === "nl" ? "en" : "nl";
    if (currentSourceValue.trim().length === 0) return;
    if (disabled) return;

    // Last-edit-wins: the field the user just blurred is treated as the new
    // source of truth, and we update the *other* side regardless of its
    // current content. To override manually, focus the other field and edit
    // it — that becomes the new source on its blur.
    setTranslatingTo(targetLang);
    setError(null);
    setNeedsKey(false);
    try {
      const res = await api.translate({
        source_lang: sourceLang,
        target_lang: targetLang,
        text: currentSourceValue,
        kind,
      });
      // Bail out if the user has already started typing in the target side
      // since this request was kicked off — we don't want to clobber a
      // concurrent manual edit mid-stream.
      const currentTarget = targetLang === "en" ? props.valueEn : props.valueNl;
      if (currentTarget !== (sourceLang === "nl" ? valueEn : valueNl)) {
        return;
      }
      onChange(targetLang, res.text);
    } catch (err) {
      if (err instanceof ApiError && err.status === 412) {
        setNeedsKey(true);
      } else {
        setError(
          err instanceof ApiError
            ? (err.body?.detail ?? err.body?.title ?? `HTTP ${err.status}`)
            : err instanceof Error
              ? err.message
              : String(err),
        );
      }
    } finally {
      setTranslatingTo(null);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {activeLang === "nl" ? (
        <FieldBox
          label={labelNl}
          lang="nl"
          value={valueNl}
          multiline={multiline}
          disabled={disabled}
          translating={translatingTo === "nl"}
          onFocus={onFocus}
          onBlur={(v) => onBlur("nl", v)}
          onChange={(v) => onChange("nl", v)}
        />
      ) : (
        <FieldBox
          label={labelEn}
          lang="en"
          value={valueEn}
          multiline={multiline}
          disabled={disabled}
          translating={translatingTo === "en"}
          onFocus={onFocus}
          onBlur={(v) => onBlur("en", v)}
          onChange={(v) => onChange("en", v)}
        />
      )}
      {error && (
        <p className="text-(--ld-negative) text-xs">
          {t("attributePanel.bilingual.translateFailed", { error })}
        </p>
      )}
      {needsKey && (
        <p className="text-(--ld-notice-ink) text-xs">{t("settings.aiAuth.notConfigured")}</p>
      )}
    </div>
  );
}

function FieldBox({
  label,
  value,
  multiline,
  disabled,
  translating,
  onFocus,
  onBlur,
  onChange,
}: {
  label: string;
  lang: Lang;
  value: string;
  multiline?: boolean | undefined;
  disabled?: boolean | undefined;
  translating: boolean;
  onFocus: () => void;
  onBlur: (value: string) => void;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const sharedProps = {
    className: multiline ? `${inputClass} min-h-[60px] resize-y` : inputClass,
    value,
    disabled,
    onFocus,
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => onBlur(e.target.value),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(e.target.value),
  };

  return (
    <div className="relative flex flex-col gap-0.5 text-xs">
      <span className="flex items-center justify-between text-(--ld-muted)">
        <span>{label}</span>
        {translating && (
          <span className="text-(--ld-accent) text-xs">
            {t("attributePanel.bilingual.translating")}
          </span>
        )}
      </span>
      {multiline ? <textarea {...sharedProps} /> : <input type="text" {...sharedProps} />}
    </div>
  );
}
